/**
 * Tipos do contrato da API do Compilações (`docs/CONTRATO.md`).
 *
 * Os nomes seguem o JSON da API (`snake_case`) de propósito: o contrato é a
 * fonte única de verdade entre `api/` e `web/`, e traduzir cada campo para
 * camelCase só criaria um segundo vocabulário para manter em sincronia. A
 * normalização (`sources/rest/normalize.ts`) garante os tipos, não renomeia.
 */

/** Status de homologação de uma versão. */
export type StatusBuild = 'nao_homologado' | 'homologado' | 'reprovado';

export const STATUS_BUILD: StatusBuild[] = ['nao_homologado', 'homologado', 'reprovado'];

export interface LinksBuild {
  instalador: string | null;
  instalador_cliente: string | null;
}

export interface JenkinsBuild {
  job: string | null;
  build: number | null;
  url: string | null;
}

export interface BuildResumo {
  id: number;
  produto: string;
  /** "master" | "v2.<ramo>" | outras (ex. "v2.utf8"). */
  branch: string;
  ramo: string;
  versao: string;
  status: StatusBuild;
  status_motivo: string | null;
  status_por: string | null;
  status_em: string | null;
  publicado_em: string;
  links: LinksBuild;
  total_artefatos: number;
  /** É a versão atual do painel do cliente. */
  no_painel: boolean;
  /** Incidentes aberto+mitigado que afetam a versão. */
  incidentes_abertos: number;
  /** Maior severidade entre os incidentes não resolvidos; `null` sem nenhum. */
  incidente_severidade_max: SeveridadeIncidente | null;
  jenkins: JenkinsBuild | null;
}

/**
 * Tipo (extensão) do artefato no Nexus. Hoje é `exe` para quase tudo e `zip`
 * só para `Database` e `erp-instalador-resources`; fica `string` para um tipo
 * novo não quebrar a tela.
 */
export type TipoArtefato = string;

/**
 * Item do manifesto — o que o fastupdate resolve no Nexus, pelas coordenadas
 * Maven `grupo:artefato:tipo:versao`.
 */
export interface Artefato {
  grupo: string;
  artefato: string;
  tipo: TipoArtefato;
  versao: string;
}

export type TipoEvento = 'publicado' | 'republicado' | 'status' | 'painel';

export interface EventoBuild {
  id: number;
  tipo: TipoEvento;
  status_anterior: StatusBuild | null;
  status_novo: StatusBuild | null;
  motivo: string | null;
  usuario: string;
  criado_em: string;
}

export interface ArtefatoAlterado {
  artefato: string;
  tipo: TipoArtefato;
  de: string;
  para: string;
}

export interface ArtefatoDiff {
  artefato: string;
  tipo: TipoArtefato;
  versao: string;
}

export interface DiffAnterior {
  /** `null` se for a primeira versão do produto/branch. */
  versao_anterior: string | null;
  alterados: ArtefatoAlterado[];
  adicionados: ArtefatoDiff[];
  removidos: ArtefatoDiff[];
}

export interface ElegibilidadePainel {
  ok: boolean;
  motivo: string | null;
}

export interface BuildDetalhe extends BuildResumo {
  artefatos: Artefato[];
  eventos: EventoBuild[];
  diff_anterior: DiffAnterior;
  elegivel_painel: ElegibilidadePainel;
  /** Todos os incidentes que afetam a versão: não resolvidos primeiro (severidade desc), depois os resolvidos. */
  incidentes: IncidenteResumo[];
}

export interface Me {
  usuario: string;
  nome: string;
  capabilities: string[];
  /** E-mail @nasajon.com.br (não é cliente externo). */
  colaborador: boolean;
  /** Acesso à NAVEGAÇÃO de cada funcionalidade (`oculto` é false para todos). */
  acesso: Acesso;
}

/* ------------------------------------------------------------------ */
/* Visibilidade (quem vê cada funcionalidade e cada ramo)              */
/* ------------------------------------------------------------------ */

export type Funcionalidade = 'feed' | 'ramos' | 'incidentes' | 'painel';

export const FUNCIONALIDADES: Funcionalidade[] = ['feed', 'ramos', 'incidentes', 'painel'];

export type Acesso = Record<Funcionalidade, boolean>;

export type Audiencia = 'publico' | 'colaboradores' | 'oculto';

export const AUDIENCIAS: Audiencia[] = ['publico', 'colaboradores', 'oculto'];

export interface VisibilidadeFuncionalidade {
  funcionalidade: Funcionalidade;
  audiencia: Audiencia;
  atualizado_por: string | null;
  atualizado_em: string | null;
}

export interface VisibilidadeRamo {
  produto: string;
  ramo: string;
  audiencia: Audiencia;
  total_builds: number;
  atualizado_por: string | null;
  atualizado_em: string | null;
}

export interface Visibilidade {
  funcionalidades: VisibilidadeFuncionalidade[];
  ramos: VisibilidadeRamo[];
  audiencias: Audiencia[];
}

export interface EventoVisibilidade {
  id: number;
  /** `funcionalidade:feed` ou `ramo:<produto>:<ramo>`. */
  alvo: string;
  audiencia_anterior: Audiencia | null;
  audiencia_nova: Audiencia;
  usuario: string;
  criado_em: string;
}

export interface Resumo {
  total_builds: number;
  ultimas_24h: number;
  nao_homologadas: number;
  incidentes_abertos: number;
  painel: { versao: string | null; desde: string | null };
}

export interface Ramo {
  ramo: string;
  branches: string[];
  total: number;
  nao_homologadas: number;
  homologados: number;
  reprovados: number;
  ultima_versao: string | null;
  ultima_publicacao: string | null;
  ultima_homologada: string | null;
}

export interface PublicacaoPainel {
  versao: string;
  versao_anterior: string | null;
  usuario: string;
  criado_em: string;
}

export interface Painel {
  modo: 'local' | 'atendimento';
  versao_atual: string | null;
  atualizado_em: string | null;
  build: BuildResumo | null;
  historico: PublicacaoPainel[];
}

export interface FiltrosFeed {
  produto?: string | null;
  ramo?: string | null;
  branch?: string | null;
  status?: StatusBuild[];
  /** Trecho da versão. */
  q?: string | null;
  /** `true` = só versões com incidente aberto/mitigado. */
  com_incidente?: boolean;
  /** Publicada a partir de (YYYY-MM-DD, dia civil de São Paulo, inclusivo). */
  publicado_de?: string | null;
  /** Publicada até (YYYY-MM-DD, inclusivo). */
  publicado_ate?: string | null;
  /** 1–100, padrão 30. */
  limit?: number;
}

export interface PaginaBuilds {
  items: BuildResumo[];
  next_cursor: string | null;
}

/**
 * Porta da camada de dados. `rest` (API real) e `demo` (fixtures em memória)
 * implementam a mesma interface; nenhuma tela sabe qual das duas está montada.
 */
/* ------------------------------------------------------------------ */
/* Incidentes (bugs conhecidos por versão)                             */
/* ------------------------------------------------------------------ */

export type SeveridadeIncidente = 'baixa' | 'media' | 'alta' | 'critica';

/** Da menos para a mais grave. */
export const SEVERIDADES: SeveridadeIncidente[] = ['baixa', 'media', 'alta', 'critica'];

export type StatusIncidente = 'aberto' | 'mitigado' | 'resolvido';

export const STATUS_INCIDENTE: StatusIncidente[] = ['aberto', 'mitigado', 'resolvido'];

export interface IncidenteResumo {
  id: number;
  produto: string;
  titulo: string;
  severidade: SeveridadeIncidente;
  status: StatusIncidente;
  link: string | null;
  /** Versão que corrige o incidente (não precisa existir como build). */
  corrigido_em: string | null;
  criado_por: string;
  criado_em: string;
  atualizado_em: string;
  resolvido_em: string | null;
  resolvido_por: string | null;
  total_versoes: number;
}

export interface VersaoDoIncidente {
  versao: string;
  branch: string;
  ramo: string;
  /** Status de homologação da VERSÃO (não do incidente). */
  status: StatusBuild;
  adicionado_por: string;
  adicionado_em: string;
}

export type TipoEventoIncidente = 'criado' | 'editado' | 'status' | 'versao_adicionada' | 'versao_removida';

export interface EventoIncidente {
  id: number;
  tipo: TipoEventoIncidente;
  /** Formato por tipo — ver `docs/CONTRATO.md`; traduzido em `features/incidentes/evento-incidente.ts`. */
  detalhe: Record<string, unknown>;
  usuario: string;
  criado_em: string;
}

export interface IncidenteDetalhe extends IncidenteResumo {
  descricao: string;
  versoes: VersaoDoIncidente[];
  /** Mais recentes primeiro. */
  eventos: EventoIncidente[];
}

export interface FiltrosIncidentes {
  produto?: string | null;
  status?: StatusIncidente[];
  severidade?: SeveridadeIncidente[];
  /** Incidentes que afetam esta versão. */
  versao?: string | null;
  /** Trecho do título. */
  q?: string | null;
  /** Criado a partir de (YYYY-MM-DD, inclusivo). */
  criado_de?: string | null;
  criado_ate?: string | null;
  /** 1–100, padrão 30. */
  limit?: number;
}

export interface PaginaIncidentes {
  items: IncidenteResumo[];
  next_cursor: string | null;
}

export interface NovoIncidente {
  titulo: string;
  descricao?: string | null;
  severidade: SeveridadeIncidente;
  link?: string | null;
  corrigido_em?: string | null;
  versoes: string[];
  produto?: string;
}

/** Edição parcial; `link`/`corrigido_em` aceitam `null` para limpar. */
export interface EdicaoIncidente {
  titulo?: string;
  descricao?: string;
  severidade?: SeveridadeIncidente;
  link?: string | null;
  corrigido_em?: string | null;
  status?: StatusIncidente;
}

/* ------------------------------------------------------------------ */
/* Administração de capabilities (`/v1/admin/capabilities`)            */
/* ------------------------------------------------------------------ */

export interface UsuarioKeycloak {
  keycloak_user_id: string;
  email: string;
  username: string;
  nome: string;
}

export interface UsuarioComCapabilities extends UsuarioKeycloak {
  capabilities: string[];
}

export interface CapabilitiesDoUsuario {
  keycloak_user_id: string;
  email: string;
  capabilities: string[];
}

export type AcaoCapability = 'conceder' | 'revogar';

/** Linha da auditoria: quem (`ator`) concedeu/revogou o quê a quem (`alvo_*`). */
export interface EventoCapability {
  id: number;
  ator: string;
  alvo_id: string;
  alvo_email: string | null;
  capability: string;
  acao: AcaoCapability;
  criado_em: string;
}

export interface CompilacoesDataSource {
  readonly kind: 'rest' | 'demo';
  me(signal?: AbortSignal): Promise<Me>;
  resumo(signal?: AbortSignal): Promise<Resumo>;
  listarBuilds(filtros: FiltrosFeed, cursor: string | null, signal?: AbortSignal): Promise<PaginaBuilds>;
  obterBuild(versao: string, produto?: string, signal?: AbortSignal): Promise<BuildDetalhe>;
  alterarStatus(versao: string, status: StatusBuild, motivo: string | null, produto?: string): Promise<BuildDetalhe>;
  listarRamos(produto?: string, signal?: AbortSignal): Promise<Ramo[]>;
  obterPainel(signal?: AbortSignal): Promise<Painel>;
  publicarPainel(versao: string): Promise<Painel>;

  // --- Incidentes (ler: qualquer usuário; escrever: maintainer/admin; apagar: sysadmin) ---
  listarIncidentes(filtros: FiltrosIncidentes, cursor: string | null, signal?: AbortSignal): Promise<PaginaIncidentes>;
  obterIncidente(id: number, signal?: AbortSignal): Promise<IncidenteDetalhe>;
  listarIncidentesDaVersao(versao: string, produto?: string, signal?: AbortSignal): Promise<IncidenteResumo[]>;
  criarIncidente(dados: NovoIncidente): Promise<IncidenteDetalhe>;
  editarIncidente(id: number, edicao: EdicaoIncidente): Promise<IncidenteDetalhe>;
  adicionarVersoesAoIncidente(id: number, versoes: string[]): Promise<IncidenteDetalhe>;
  removerVersaoDoIncidente(id: number, versao: string): Promise<IncidenteDetalhe>;
  apagarIncidente(id: number): Promise<void>;

  // --- Administração de capabilities (exige `admin`; `sysadmin` só por sysadmin) ---
  listarUsuariosComCapabilities(signal?: AbortSignal): Promise<UsuarioComCapabilities[]>;
  buscarUsuariosKeycloak(query: string, signal?: AbortSignal): Promise<UsuarioKeycloak[]>;
  obterCapabilitiesDoUsuario(keycloakUserId: string, signal?: AbortSignal): Promise<CapabilitiesDoUsuario>;
  concederCapability(keycloakUserId: string, capability: string): Promise<void>;
  revogarCapability(keycloakUserId: string, capability: string): Promise<void>;
  listarEventosCapabilities(limit?: number, signal?: AbortSignal): Promise<EventoCapability[]>;

  // --- Visibilidade (exige `admin`) ---
  listarVisibilidade(signal?: AbortSignal): Promise<Visibilidade>;
  alterarVisibilidadeFuncionalidade(funcionalidade: Funcionalidade, audiencia: Audiencia): Promise<VisibilidadeFuncionalidade>;
  alterarVisibilidadeRamo(produto: string, ramo: string, audiencia: Audiencia): Promise<VisibilidadeRamo>;
  listarEventosVisibilidade(limit?: number, signal?: AbortSignal): Promise<EventoVisibilidade[]>;
}
